/**
 * InboxTabs — 4-tab pill selector with unread count badges.
 */

import React from 'react'
import { motion } from 'framer-motion'

export type InboxTab = 'all' | 'direct' | 'comments' | 'notifications'

interface InboxTabsProps {
  tabs: Array<{ id: InboxTab; label: string; count?: number }>
  activeTab: InboxTab
  onTabChange: (tab: InboxTab) => void
}

export const InboxTabs: React.FC<InboxTabsProps> = ({ tabs, activeTab, onTabChange }) => (
  <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto scrollbar-hide">
    {tabs.map((tab) => {
      const isActive = activeTab === tab.id
      return (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`
            relative flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap
            text-[13px] font-semibold transition-colors tap-highlight-transparent shrink-0
            ${isActive ? 'text-text' : 'text-text-hint/50 hover:text-text-hint'}
          `}
        >
          {isActive && (
            <motion.div
              layoutId="inboxTabPill"
              className="absolute inset-0 bg-white/[0.08] rounded-full border border-white/[0.06]"
              transition={{ type: 'spring', stiffness: 350, damping: 28 }}
            />
          )}
          <span className="relative z-10">{tab.label}</span>
          {(tab.count ?? 0) > 0 && (
            <span className={`
              relative z-10 min-w-[18px] h-[18px] flex items-center justify-center
              rounded-full text-[10px] font-bold px-1
              ${isActive ? 'bg-primary text-white' : 'bg-white/[0.08] text-text-hint'}
            `}>
              {(tab.count ?? 0) > 99 ? '99+' : tab.count}
            </span>
          )}
        </button>
      )
    })}
  </div>
)
